import { ChevronDown, Loader2, Radio, Settings2, Wallet } from 'lucide-react';
import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Kbd } from '@/components/ui/kbd';
import { Panel } from '@/components/ui/panel';
import { useCustodialPanelState } from '@/features/custodial';
import { useSessionResolved } from '@/features/tradingSession/useSessionResolved';
import { ModeBanner, WalletConnectControl } from '@/features/wallet';
import { cn } from '@/lib/utils';

import { useTradingStore } from '../store';

import { useCommandPaletteState } from './CommandPalette';

const NAV_ITEM =
    'cursor-pointer text-xs font-bold uppercase tracking-[0.08em] transition-colors';

/** A nav entry that goes somewhere, lit when you are already there. */
const navClass = ({ isActive }: { isActive: boolean }) =>
    cn(
        NAV_ITEM,
        isActive
            ? 'text-foreground'
            : 'text-muted-foreground hover:text-foreground',
    );

/**
 * A nav entry that does not. These are visual placeholders for sections that
 * are designed but not built; each is hidden from assistive tech individually
 * rather than by hiding the whole nav, which is what the row did back when
 * nothing in it worked.
 */
const NavPreview: React.FC<{ label: string }> = ({ label }) => (
    <span
        aria-hidden="true"
        title="Preview, not built yet"
        className={cn(NAV_ITEM, 'cursor-default text-muted-foreground')}
    >
        {label}
    </span>
);

/**
 * Header reads all state from the trading store and custodial panel store (0 props).
 */
const Header: React.FC = React.memo(() => {
    const runtimeMode = useTradingStore((s) => s.runtimeMode);
    const balance = useTradingStore((s) => s.balance);
    // Same three custodial flags the desktop rail reads: in real mode the
    // balance is not authoritative for the first ~10-15s, so a bare "$0.00"
    // here would lie to mobile users.
    const isCustodialBalanceLoading = useTradingStore(
        (s) => s.isCustodialBalanceLoading,
    );
    const isCustodialBalanceSettling = useTradingStore(
        (s) => s.isCustodialBalanceSettling,
    );
    const isCustodialBalanceProvisional = useTradingStore(
        (s) => s.isCustodialBalanceProvisional,
    );
    const isRealMode = runtimeMode === 'real';
    // the store boots as 'demo' before anyone has asked the server; showing
    // that would flash "Demo" at a signed-in user on every load.
    const sessionResolved = useSessionResolved();
    const displayedMode = sessionResolved ? runtimeMode : null;

    const openDeposit = useCustodialPanelState((s) => s.openDeposit);
    const navigate = useNavigate();
    const { pathname } = useLocation();
    // The custodial panel is mounted by the terminal, not by this header, so
    // asking for it from another route has to land back on the terminal. The
    // store already holds the request by then, so the panel opens on arrival.
    const manageFunds = () => {
        openDeposit();
        if (pathname !== '/') void navigate('/');
    };

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    return (
        <Panel
            as="header"
            className="@container px-2 py-1.5 @[640px]:px-4 @[640px]:py-2.5"
            style={{ gridArea: 'header' }}
        >
            <Collapsible open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <div className="flex items-center justify-between gap-2">
                    {/* Logo */}
                    <div className="flex shrink-0 items-center gap-2 @[640px]:gap-2.5">
                        <img
                            src="/snappy-logo.png"
                            alt=""
                            aria-hidden="true"
                            className="h-6 w-auto @[640px]:h-7"
                        />
                        {/* Wordmark needs room the smallest phones don't have —
                            the logo alone carries the brand there. Kept
                            sr-only (not hidden) so the page always has an h1. */}
                        <h1 className="sr-only font-sans text-lg font-semibold tracking-tight text-foreground @[480px]:not-sr-only @[640px]:text-xl">
                            Snappy
                            <span className="text-muted-foreground">Trade</span>
                        </h1>
                    </div>

                    {/* Center nav. Trade and Live Trades are real routes; the
                        two between them are still placeholders. gap-6 rather
                        than the gap-8 this row had with three items: a fourth
                        entry has to fit beside the wallet controls at the
                        820px the nav appears at. */}
                    <nav
                        aria-label="Sections"
                        className="hidden items-center gap-6 @[820px]:flex"
                    >
                        <NavLink to="/" end className={navClass}>
                            Trade
                        </NavLink>
                        <NavPreview label="Referrals" />
                        <NavPreview label="Snappy Cup" />
                        <NavLink to="/live" className={navClass}>
                            <span className="flex items-center gap-1.5">
                                <span
                                    aria-hidden="true"
                                    className="inline-block size-1.5 animate-pulse rounded-full bg-primary"
                                />
                                Live Trades
                            </span>
                        </NavLink>
                    </nav>

                    {/* Desktop controls — single row. Market selection lives
                        in the SpeedChartHeader strip below, at every width. */}
                    <div className="hidden items-center gap-3 @[640px]:flex">
                        {/* Between 640px and 820px the nav above is hidden and
                            the mobile menu below has not appeared: without this
                            the tape would be unreachable in that band. */}
                        <NavLink
                            to="/live"
                            title="Live trades"
                            aria-label="Live trades"
                            className={({ isActive }) =>
                                cn(
                                    'rounded-md transition-colors @[820px]:hidden',
                                    isActive
                                        ? 'text-primary'
                                        : 'text-muted-foreground hover:text-foreground',
                                )
                            }
                        >
                            <Radio size={15} />
                        </NavLink>
                        <button
                            type="button"
                            onClick={() =>
                                useCommandPaletteState.getState().setOpen(true)
                            }
                            title="Command palette"
                            className="cursor-pointer rounded-md text-muted-foreground transition-colors hover:text-foreground"
                        >
                            <Kbd>⌘K</Kbd>
                        </button>
                        <ModeBanner mode={displayedMode} className="h-8" />
                        <WalletConnectControl className="h-8" />
                    </div>

                    {/* Mobile controls — compact row + menu toggle. This row
                        also appears on wide desktop when the sidebar squeezes
                        the header below its 640px container breakpoint. */}
                    <div className="flex items-center gap-2 @[640px]:hidden">
                        <WalletConnectControl compact className="h-8" />
                        <CollapsibleTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                aria-label={
                                    mobileMenuOpen ? 'Close menu' : 'Open menu'
                                }
                                className="size-8 text-muted-foreground hover:text-foreground"
                            >
                                <ChevronDown
                                    className={cn(
                                        'size-4 transition-transform duration-200',
                                        mobileMenuOpen && 'rotate-180',
                                    )}
                                />
                            </Button>
                        </CollapsibleTrigger>
                    </div>
                </div>

                {/* Mobile collapsible panel */}
                <CollapsibleContent className="@[640px]:hidden">
                    {/* The sections, for the widths the nav above cannot show.
                        Closing the menu on navigation matters here: routing
                        away leaves this header mounted, so an open menu would
                        stay open over the page it just took you to. */}
                    <nav
                        aria-label="Sections"
                        className="mt-2 flex items-center gap-5 border-t border-border/40 pt-2"
                    >
                        <NavLink
                            to="/"
                            end
                            onClick={() => setMobileMenuOpen(false)}
                            className={navClass}
                        >
                            Trade
                        </NavLink>
                        <NavLink
                            to="/live"
                            onClick={() => setMobileMenuOpen(false)}
                            className={navClass}
                        >
                            <span className="flex items-center gap-1.5">
                                <span
                                    aria-hidden="true"
                                    className="inline-block size-1.5 animate-pulse rounded-full bg-primary"
                                />
                                Live Trades
                            </span>
                        </NavLink>
                    </nav>
                    <div className="mt-2 flex items-center gap-3 border-t border-border/40 pt-2">
                        <ModeBanner mode={displayedMode} className="h-7" />
                        <div className="flex items-center gap-1.5">
                            <Wallet
                                size={12}
                                className="text-muted-foreground"
                            />
                            {isCustodialBalanceLoading ? (
                                <Loader2
                                    size={12}
                                    className="animate-spin text-muted-foreground"
                                />
                            ) : (
                                <span
                                    className={cn(
                                        'font-mono text-xs font-semibold tabular-nums',
                                        balance === 0
                                            ? 'text-muted-foreground'
                                            : 'text-foreground',
                                        isRealMode &&
                                            isCustodialBalanceSettling &&
                                            'animate-pulse opacity-60',
                                        // read succeeded, trading authority still
                                        // resolving: show the real number, dimmed.
                                        isCustodialBalanceProvisional &&
                                            'opacity-50',
                                    )}
                                >
                                    ${balance.toFixed(2)}
                                </span>
                            )}
                            {isRealMode && (
                                <button
                                    onClick={manageFunds}
                                    className="flex size-6 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
                                    title="Manage funds"
                                    aria-label="Manage funds"
                                >
                                    <Settings2 size={10} />
                                </button>
                            )}
                        </div>
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </Panel>
    );
});

Header.displayName = 'Header';

export default Header;
